import type { LikertConfig, MCQConfig, NPSConfig, PolarConfig, Question, Response, SegmentRow } from "./types";
import { computeMcqDistribution, computeNpsScore, computePolarSplit } from "./cohorts";

/**
 * Pure view models for the stage results views. Components in
 * src/components/Stage/views/ render these and contain layout only.
 */

export type RespondentBase = { respondents: number; unlinked: number } | null;

export type McqRow = { key: string; label: string; count: number; pct: number; isOther: boolean };
export type McqModel = { rows: McqRow[]; base: number; baseLabel: string; multi: boolean };

function pctOf(count: number, base: number): number {
  return base === 0 ? 0 : Math.round((count / base) * 100);
}

/** "von N Befragten" / "von N Nennungen" — the base every percentage refers to. */
function baseFor(question: Question, responses: Response[], base: RespondentBase): { base: number; label: string } {
  const multi = (question.config as MCQConfig).multi_select === true;
  const fromStats = base ? base.respondents + base.unlinked : 0;
  if (fromStats > 0) return { base: fromStats, label: `von ${fromStats} Befragten` };
  if (multi) return { base: responses.length, label: `von ${responses.length} Nennungen` };
  return { base: responses.length, label: `von ${responses.length} Befragten` };
}

export function mcqModel(question: Question, responses: Response[], base: RespondentBase): McqModel {
  const dist = computeMcqDistribution(question, responses);
  const b = baseFor(question, responses, base);
  const rows = dist
    .map((d) => ({ key: d.key, label: d.label, count: d.count, pct: pctOf(d.count, b.base), isOther: d.is_other }))
    .sort((x, y) => {
      if (x.isOther !== y.isOther) return x.isOther ? 1 : -1;
      return y.count - x.count;
    });
  return { rows, base: b.base, baseLabel: b.label, multi: (question.config as MCQConfig).multi_select === true };
}

export type ScaleBin = { value: number; count: number; pct: number };
export type ScaleBands = { low: number; mid: number; high: number; lowLabel: string; midLabel: string; highLabel: string };
export type ScaleModel = {
  bins: ScaleBin[];
  total: number;
  hero: string;
  heroCaption: string;
  minLabel: string;
  maxLabel: string;
  bands: ScaleBands;
};

export function formatDecimal(n: number): string {
  return n.toFixed(1).replace(".", ",");
}

/** Band of a scale value: NPS 0–6 / 7–8 / 9–10, Likert 1–2 / 3 / 4–5. */
export function bandOf(question: Question, value: number): "low" | "mid" | "high" {
  if (question.question_type === "nps") return value <= 6 ? "low" : value <= 8 ? "mid" : "high";
  return value <= 2 ? "low" : value === 3 ? "mid" : "high";
}

export function scaleModel(question: Question, responses: Response[]): ScaleModel {
  const isNps = question.question_type === "nps";
  const cfg = question.config as NPSConfig | LikertConfig;
  const min = isNps ? 0 : (cfg.scale?.min ?? 1);
  const max = isNps ? 10 : (cfg.scale?.max ?? 5);
  const rated = responses.filter((r) => r.rating != null && r.rating >= min && r.rating <= max);
  const total = rated.length;
  const bins: ScaleBin[] = [];
  for (let v = min; v <= max; v++) {
    const count = rated.filter((r) => r.rating === v).length;
    bins.push({ value: v, count, pct: pctOf(count, total) });
  }
  const bands: ScaleBands = {
    low: 0,
    mid: 0,
    high: 0,
    lowLabel: isNps ? "Detraktoren" : "Kritisch",
    midLabel: isNps ? "Passiv" : "Mitte",
    highLabel: isNps ? "Promotoren" : "Zustimmend",
  };
  for (const r of rated) bands[bandOf(question, r.rating as number)] += 1;

  let hero = "–";
  let heroCaption: string;
  if (isNps) {
    heroCaption = "Net Promoter Score";
    if (total > 0) {
      const score = computeNpsScore(rated);
      hero = score > 0 ? `+${score}` : String(score);
    }
  } else {
    heroCaption = `Durchschnitt von ${max}`;
    if (total > 0) hero = formatDecimal(rated.reduce((s, r) => s + (r.rating ?? 0), 0) / total);
  }
  return { bins, total, hero, heroCaption, minLabel: cfg.labels?.min ?? "", maxLabel: cfg.labels?.max ?? "", bands };
}

export type PolarSideModel = { key: string; label: string; count: number; pct: number };
export type PolarModel = { a: PolarSideModel; b: PolarSideModel; total: number };

export function polarModel(question: Question, responses: Response[]): PolarModel {
  const cfg = question.config as PolarConfig;
  const s = computePolarSplit(question, responses);
  return {
    a: { key: cfg.side_a.key, label: cfg.side_a.label, count: s.a_count, pct: s.a_pct },
    b: { key: cfg.side_b.key, label: cfg.side_b.label, count: s.b_count, pct: s.b_pct },
    total: s.a_count + s.b_count,
  };
}

export type CompareSegment = { key: string; label: string; respondents: number; values: Record<string, number> };
export type CompareModel = { available: boolean; segments: CompareSegment[] };

export const UNLINKED_SEGMENT = "__unlinked__";

/** Human-readable label of a choice or polar answer; null for open_text and
 *  scale questions (their answer is the numeric rating). */
export function answerLabel(
  question: Question,
  response: Pick<Response, "choice_key" | "is_other">,
): string | null {
  if (question.question_type === "multiple_choice") {
    const cfg = question.config as MCQConfig;
    if (response.is_other === true || response.choice_key === "__other__") {
      return cfg.other_label || "Sonstiges";
    }
    if (!response.choice_key) return null;
    return (cfg.options ?? []).find((o) => o.key === response.choice_key)?.label ?? response.choice_key;
  }
  if (question.question_type === "polar") {
    const cfg = question.config as PolarConfig;
    if (response.choice_key === cfg.side_a?.key) return cfg.side_a.label;
    if (response.choice_key === cfg.side_b?.key) return cfg.side_b.label;
    return response.choice_key ?? null;
  }
  return null;
}

/** JSON-friendly distribution for exports — the same models the stage renders. */
export function exportDistribution(
  question: Question,
  responses: Response[],
  base: RespondentBase,
): unknown | null {
  switch (question.question_type) {
    case "multiple_choice": {
      const m = mcqModel(question, responses, base);
      return { rows: m.rows, base: m.base, base_label: m.baseLabel, multi_select: m.multi };
    }
    case "likert":
    case "nps": {
      const m = scaleModel(question, responses);
      return {
        total: m.total,
        hero: m.hero,
        hero_caption: m.heroCaption,
        bins: m.bins,
        bands: {
          low: m.bands.low,
          mid: m.bands.mid,
          high: m.bands.high,
          low_label: m.bands.lowLabel,
          mid_label: m.bands.midLabel,
          high_label: m.bands.highLabel,
        },
      };
    }
    case "polar": {
      const m = polarModel(question, responses);
      return { a: m.a, b: m.b, total: m.total };
    }
    default:
      return null;
  }
}

/** Segment rows from the RPC → labelled segments in the segment question's option order,
 *  "Ohne Zuordnung" last. `available` is false when the RPC returned nothing. */
export function compareModel(segmentQuestion: Question | null, rows: SegmentRow[]): CompareModel {
  if (!segmentQuestion || rows.length === 0) return { available: false, segments: [] };
  const options = (segmentQuestion.config as MCQConfig).options ?? [];
  const byKey = new Map<string, CompareSegment>();
  for (const row of rows) {
    const seg = byKey.get(row.segment_key) ?? {
      key: row.segment_key,
      label:
        row.segment_key === UNLINKED_SEGMENT
          ? "Ohne Zuordnung"
          : options.find((o) => o.key === row.segment_key)?.label ?? row.segment_key,
      respondents: row.segment_respondents,
      values: {},
    };
    seg.values[row.value_key] = (seg.values[row.value_key] ?? 0) + row.n;
    byKey.set(row.segment_key, seg);
  }
  const order = (key: string) => {
    if (key === UNLINKED_SEGMENT) return Number.MAX_SAFE_INTEGER;
    const i = options.findIndex((o) => o.key === key);
    return i === -1 ? options.length : i;
  };
  const segments = [...byKey.values()].sort((x, y) => order(x.key) - order(y.key));
  return { available: true, segments };
}
